import { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell,
  toast 
} from '@etherisc/ui-kit';
import { MapIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, XCircleIcon } from 'lucide-react';
import type { WizardStepProps } from '../../../framework/wizard';
import type { QuoteWizardData } from '../QuoteWizard';
import { createCompleteQuote } from '../utils/quoteHelpers';
import { quoteService } from '../../../services/quoteService';

import { LocationMap } from '../components/LocationMap';

export function LocationVerificationStepNew({ 
  data, 
  updateData
}: WizardStepProps<QuoteWizardData>) {
  const [isValidating, setIsValidating] = useState(false);
  const [validationComplete, setValidationComplete] = useState(false);
  const [isValidatingGeo, setIsValidatingGeo] = useState(false);
  const [geoValidationResults, setGeoValidationResults] = useState<Array<{
    farmerId: string;
    farmerName: string;
    status: string;
    details: { reason: string };
  }> | null>(null);

  // Note: Using shared createCompleteQuote utility to avoid code duplication

  // Note: Removed unused errors parameter - validation now handled by SimpleWizardShell

  // Extract real location data from participants
  const [locations, setLocations] = useState(() => {
    const allParticipants: Array<{
      id: string;
      name: string;
      lat: number;
      lng: number;
      status: 'valid' | 'invalid' | 'pending';
      farmer: Record<string, unknown>;
      role: string;
      sumInsured: number;
      hectaresAnimalCount?: number;
    }> = [];
    
    // Build from quote.participants (single source of truth)
    if (data.quote?.participants) {
      data.quote.participants.forEach(participant => {
        if (participant.farmer?.latitude != null && participant.farmer?.longitude != null) {
          // Determine status based on stored validation data
          const isInvalidLocation = data.quote?.locationValidation?.invalid_locations?.some(loc => loc.farmer_id === participant.farmerId);
          const status = data.quote?.locationValidation?.validation_performed 
            ? (isInvalidLocation ? 'invalid' : 'valid')
            : 'pending';
          
          allParticipants.push({
            id: participant.farmerId || '',
            name: participant.farmer?.name || '', // Clean name without suffix
            lat: Number(participant.farmer?.latitude) || 0,
            lng: Number(participant.farmer?.longitude) || 0,
            status: status as 'valid' | 'invalid' | 'pending',
            farmer: participant.farmer as unknown as Record<string, unknown>,
            role: participant.role,
            sumInsured: Number(participant.sumInsured) || 0,
            hectaresAnimalCount: participant.hectaresAnimalCount ? Number(participant.hectaresAnimalCount) : undefined
          });
        }
      });
    }
    
    return allParticipants;
  });

  // Update locations when participants change (e.g., after bulk upload)
  useEffect(() => {
    const allParticipants: Array<{
      id: string;
      name: string;
      lat: number;
      lng: number;
      status: 'valid' | 'invalid' | 'pending';
      farmer: Record<string, unknown>;
      role: string;
      sumInsured: number;
      hectaresAnimalCount?: number;
    }> = [];
    
    if (data.quote?.participants) {
      data.quote.participants.forEach(participant => {
        // Include all farmer participants (even without coordinates)
        if (participant.farmerId && participant.farmer) {
          const hasCoordinates = participant.farmer?.latitude != null && participant.farmer?.longitude != null;
          const isInvalidLocation = data.quote?.locationValidation?.invalid_locations?.some(loc => loc.farmer_id === participant.farmerId);
          const status = !hasCoordinates ? 'pending' :
            (data.quote?.locationValidation?.validation_performed 
              ? (isInvalidLocation ? 'invalid' : 'valid')
              : 'pending');
          
          allParticipants.push({
            id: participant.farmerId || '',
            name: participant.farmer?.name || '',
            lat: Number(participant.farmer?.latitude) || 0,
            lng: Number(participant.farmer?.longitude) || 0,
            status: status as 'valid' | 'invalid' | 'pending',
            farmer: participant.farmer as unknown as Record<string, unknown>,
            role: participant.role,
            sumInsured: Number(participant.sumInsured) || 0,
            hectaresAnimalCount: participant.hectaresAnimalCount ? Number(participant.hectaresAnimalCount) : undefined
          });
        }
      });
    }
    
    setLocations(allParticipants);
  }, [data.quote?.participants]);

  useEffect(() => {
    // Check if we have location validation data
    
    if (data.quote?.locationValidation?.validation_performed) {
      setValidationComplete(true);
    }
  }, [data.quote?.locationValidation, validationComplete]);

  // Update locations status when validation data is available
  useEffect(() => {
    if (data.quote?.locationValidation?.validation_performed && locations.length > 0) {
      const updatedLocations = locations.map(location => {
        const isInvalidLocation = data.quote?.locationValidation?.invalid_locations?.some(loc => loc.farmer_id === location.id);
        const newStatus = isInvalidLocation ? 'invalid' : 'valid';
        
        return {
          ...location,
          status: newStatus as 'valid' | 'invalid' | 'pending'
        };
      });
      
      
      setLocations(updatedLocations);
    }
  }, [data.quote?.locationValidation?.validation_performed, data.quote?.locationValidation?.invalid_locations, locations.length]);

  // Debug: Track locations state and full localStorage data
  useEffect(() => {
    
  }, [locations, validationComplete, data.quote?.locationValidation]);

  // Note: Removed backend fetch during navigation - violates intended flow
  // Navigation should only use Zustand/localStorage data
  // Backend fetch should only happen when data is dirty or user explicitly saves

  // Update locations when participant data changes - USE SINGLE SOURCE OF TRUTH
  useEffect(() => {
    // Build locations from quote.participants (single source of truth)
    const allParticipants: Array<{
      id: string;
      name: string;
      lat: number;
      lng: number;
      status: 'valid' | 'invalid' | 'pending';
      farmer: Record<string, unknown>;
      role: string;
      sumInsured: number;
      hectaresAnimalCount?: number;
    }> = [];
    
    if (data.quote?.participants) {
      data.quote.participants.forEach(participant => {
        // Only include participants with farmer data and location
        if (participant.farmer?.latitude != null && participant.farmer?.longitude != null) {
          // Determine status based on stored validation data
          const isInvalidLocation = data.quote?.locationValidation?.invalid_locations?.some(loc => loc.farmer_id === participant.farmerId);
          const status = data.quote?.locationValidation?.validation_performed 
            ? (isInvalidLocation ? 'invalid' : 'valid')
            : 'pending';
          
          // Use clean name without suffix - role is shown in badge column
          const displayName = participant.farmer?.name || '';
            
          allParticipants.push({
            id: participant.farmerId || '',
            name: displayName,
            lat: Number(participant.farmer?.latitude) || 0,
            lng: Number(participant.farmer?.longitude) || 0,
            status: status as 'valid' | 'invalid' | 'pending',
            farmer: {
              name: String(participant.farmer?.name || ''),
              phone: String(participant.farmer?.phone || ''),
              area: String(participant.farmer?.area || '')
            },
            role: participant.role,
            sumInsured: Number(participant.sumInsured) || 0, // Use actual sumInsured from quote.participants
            hectaresAnimalCount: participant.hectaresAnimalCount ? Number(participant.hectaresAnimalCount) : undefined
          });
        }
      });
    }
    
    
    setLocations(allParticipants as unknown as typeof locations);
  }, [data.quote?.participants, data.quote?.locationValidation]); // Use quote.participants as dependency

  const handleValidateLocations = async () => {
    setIsValidating(true);
    
    try {
      let validationData;
      
      if (data.quote?.id) {
        validationData = await quoteService.validateLocations(data.quote.id);
      } else {
        const currentInvalidLocations = locations.filter(location => {
          return location.lat === 0 && location.lng === 0 ||
                 location.lat < -22 || location.lat > -15 ||
                 location.lng < 25 || location.lng > 33;
        });
        
        validationData = {
          validation_performed: true,
          all_within_coverage: currentInvalidLocations.length === 0,
          total_locations: locations.length,
          valid_locations: locations.length - currentInvalidLocations.length,
          invalid_locations: currentInvalidLocations.map(loc => ({
            farmer_id: loc.id,
            coordinates: [loc.lat, loc.lng] as [number, number],
            distance_from_coverage: 0,
            reason: 'Outside coverage area'
          })),
          coverage_area_used: 'client-side-fallback',
          validated_at: new Date().toISOString(),
        };
      }
      
      // Update location display statuses
      const updatedLocations = locations.map(location => {
        const isInvalid = validationData!.invalid_locations.some(
          (inv: { farmer_id: string }) => inv.farmer_id === location.id
        );
        return {
          ...location,
          status: isInvalid ? 'invalid' as const : 'valid' as const
        };
      });
      
      setLocations(updatedLocations);
      
      const updatedQuote = createCompleteQuote(data, {
        locationValidation: validationData
      });
      
      updateData({ quote: updatedQuote });
      setValidationComplete(true);
    } catch (error) {
      console.error('Location validation failed:', error);
      toast.error('Location validation failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateAdminBoundaries = async () => {
    if (!data.quote?.id) {
      toast.error('Quote must be saved before validating admin boundaries');
      return;
    }
    setIsValidatingGeo(true);
    try {
      const result = await quoteService.validateQuoteGeolocations(data.quote.id);
      setGeoValidationResults(result.results as typeof geoValidationResults);
      const { summary } = result;
      if (summary.invalid > 0) {
        toast.warning(`${summary.invalid} farmer(s) outside their admin boundary`);
      } else if (summary.valid > 0) {
        toast.success('All farmers are within their admin boundaries');
      } else {
        toast.info('No farmers could be validated (missing data)');
      }
    } catch (error) {
      console.error('Admin boundary validation failed:', error);
      toast.error('Admin boundary validation failed');
    } finally {
      setIsValidatingGeo(false);
    }
  };

  // #181: Detect if farmer locations have changed since quote creation
  const [locationChangedFarmers, setLocationChangedFarmers] = useState<string[]>([]);
  
  useEffect(() => {
    if (!data.quote?.locationValidation?.validated_at || !data.quote?.participants) return;
    
    const changedFarmers: string[] = [];
    const storedInvalid = data.quote.locationValidation.invalid_locations || [];
    
    for (const participant of data.quote.participants) {
      if (!participant.farmer || !participant.farmerId) continue;
      const currentLat = Number(participant.farmer.latitude) || 0;
      const currentLng = Number(participant.farmer.longitude) || 0;
      
      // Check against stored validation coordinates
      const storedEntry = storedInvalid.find(inv => inv.farmer_id === participant.farmerId);
      if (storedEntry && storedEntry.coordinates) {
        const [storedLat, storedLng] = storedEntry.coordinates;
        if (Math.abs(currentLat - storedLat) > 0.0001 || Math.abs(currentLng - storedLng) > 0.0001) {
          changedFarmers.push(participant.farmer.name || participant.farmerId);
        }
      }
      
      // Also check valid locations: if validation was performed and farmer now has different coords
      // compared to what the map shows, flag it
      const mapLocation = locations.find(loc => loc.id === participant.farmerId);
      if (mapLocation && (Math.abs(currentLat - mapLocation.lat) > 0.0001 || Math.abs(currentLng - mapLocation.lng) > 0.0001)) {
        if (!changedFarmers.includes(participant.farmer.name || participant.farmerId)) {
          changedFarmers.push(participant.farmer.name || participant.farmerId);
        }
      }
    }
    
    setLocationChangedFarmers(changedFarmers);
    
    if (changedFarmers.length > 0) {
      updateData({ recalcNeeded: true });
    }
  }, [data.quote?.participants, data.quote?.locationValidation?.validated_at]);

  // Check if quote is approved (read-only mode)
  const isReadOnly = data.quote?.state === 'approved';

  return (
    <div className={`space-y-6 ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-lg font-medium text-gray-900">Location Verification</h4>
        </div>
        {isReadOnly && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Approved</span>
          </div>
        )}
      </div>

      {/* #181: Warning when farmer locations have changed */}
      {locationChangedFarmers.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-2">
            <AlertCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Farmer location(s) changed since last validation</p>
              <p>
                The following farmers have updated coordinates: {locationChangedFarmers.join(', ')}.
                Please re-validate locations and recalculate on Step 5.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* #138: Warning for farmers without coordinates */}
      {(() => {
        const farmersWithoutCoords = (data.quote?.participants || []).filter(p =>
          p.farmerId && p.farmer && (p.farmer.latitude == null || p.farmer.longitude == null)
        );
        if (farmersWithoutCoords.length === 0) return null;
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-2">
              <AlertCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Farmers missing location coordinates</p>
                <ul className="mt-1 space-y-1">
                  {farmersWithoutCoords.map(p => (
                    <li key={p.farmerId}>
                      Farmer {p.farmer?.name || 'Unknown'} has no location coordinates. Please add coordinates before proceeding.
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Interactive Location Map */}
      <div className="border border-gray-200 rounded-lg">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
          <h5 className="font-medium text-gray-900">Insured Locations Map</h5>
          <p className="text-sm text-gray-600">
            Farmer locations displayed with validation status: 
            <span className="inline-flex items-center ml-2 gap-4">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-green-500 rounded-full inline-block"></span>
                <span className="text-xs">Valid</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-500 rounded-full inline-block"></span>
                <span className="text-xs">Invalid</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-500 rounded-full inline-block"></span>
                <span className="text-xs">Pending</span>
              </span>
            </span>
          </p>
        </div>
        
        <div className="p-4">
          <LocationMap 
            locations={locations.map(loc => ({
              id: loc.id,
              name: loc.name,
              lat: loc.lat,
              lng: loc.lng,
              status: loc.status,
              farmer: {
                name: (loc.farmer.name as string) || '',
                phone: (loc.farmer.phone as string) || '',
                area: (loc.farmer.area as string) || ''
              }
            }))}
            height="400px"
            className="w-full"
          />
        </div>
      </div>

      {/* Location List */}
      <div className="space-y-4">
        <h5 className="font-medium text-gray-900">Insured Locations</h5>
        
        {/* Handle different policy types */}
        {data.quote?.isMasterPolicy && locations.length === 0 && data.bulkUploadData?.validRecords ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-2">
              <InfoIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Master Policy - Participants Not Loaded</p>
                <p>
                  Expected <strong>{data.bulkUploadData.validRecords}</strong> farmers 
                  from file: <strong>{data.bulkUploadData.fileName}</strong>
                </p>
                <p className="mt-2">
                  The bulk upload may still be processing, or there may be an issue loading participant data.
                </p>
              </div>
            </div>
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-2">
              <InfoIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">No Locations Available</p>
                <p>
                  Please go back to the Participants step and select farmers with valid location data.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Participants Table */}
            <div className="max-h-[800px] overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Validation Status</TableHead>
                    <TableHead className="text-right">Sum Insured</TableHead>
                    <TableHead className="text-right">Hectares/Animals Insured</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {locations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell className="font-medium">
                      {location.name}
                      {location.role === 'POLICYHOLDER' && (
                        <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                          Policyholder
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {typeof location.lat === 'number' ? location.lat.toFixed(4) : '0.0000'}, {typeof location.lng === 'number' ? location.lng.toFixed(4) : '0.0000'}
                        {(location.lat === 0 && location.lng === 0) && (
                          <span className="text-yellow-600 ml-2">(No coordinates)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center">
                        {location.status === 'valid' ? (
                          <div title="Within Coverage">
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          </div>
                        ) : location.status === 'invalid' ? (
                          <div title="Outside Coverage">
                            <AlertCircleIcon className="h-5 w-5 text-red-600" />
                          </div>
                        ) : (
                          <div title="Pending Validation">
                            <MapIcon className="h-5 w-5 text-gray-600" />
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      ${location.sumInsured?.toLocaleString() || '0'}
                    </TableCell>
                    <TableCell className="text-right">
                      {location.hectaresAnimalCount ? location.hectaresAnimalCount.toLocaleString() : '-'}
                    </TableCell>
                    <TableCell>
                      {location.status === 'invalid' && (
                        <Button
                          onClick={() => {
                            // Remove from local display
                            const updatedLocations = locations.filter(loc => loc.id !== location.id);
                            setLocations(updatedLocations);
                            
                            // Remove from quote.participants (actual data update)
                            const updatedParticipants = (data.quote?.participants || []).filter(
                              p => p.farmerId !== location.id
                            );
                            
                            // Update validation status for remaining participants
                            const remainingInvalidLocations = updatedLocations.filter(loc => loc.status === 'invalid');
                            const updatedQuote = createCompleteQuote(data, {
                              participants: updatedParticipants,
                              locationValidation: data.quote?.locationValidation?.validation_performed ? {
                                ...data.quote.locationValidation,
                                all_within_coverage: remainingInvalidLocations.length === 0,
                                total_locations: updatedLocations.length,
                                valid_locations: updatedLocations.filter(loc => loc.status === 'valid').length,
                                invalid_locations: remainingInvalidLocations.map(loc => ({
                                  farmer_id: loc.id,
                                  coordinates: [loc.lat, loc.lng] as [number, number],
                                  distance_from_coverage: 0,
                                  reason: 'Outside coverage area'
                                }))
                              } : undefined
                            });
                            updateData({ quote: updatedQuote, recalcNeeded: true });
                          }}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <XCircleIcon className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Farmers Without Location Row */}
                {(() => {
                  const farmersWithoutLocation = data.quote?.participants?.filter(p => 
                    p.farmerId && p.farmer && 
                    (p.farmer.latitude == null || p.farmer.longitude == null)
                  ) || [];
                  const sumInsuredWithoutLocation = farmersWithoutLocation.reduce((sum, p) => 
                    sum + (Number(p.sumInsured) || 0), 0
                  );
                  
                  return farmersWithoutLocation.length > 0 ? (
                    <TableRow className="bg-yellow-50">
                      <TableCell className="font-medium text-yellow-800">
                        {farmersWithoutLocation.length} farmers without location
                      </TableCell>
                      <TableCell className="text-sm text-yellow-600">No coordinates</TableCell>
                      <TableCell className="text-center">
                        <MapIcon className="h-5 w-5 text-yellow-600 mx-auto" />
                      </TableCell>
                      <TableCell className="text-right font-medium text-yellow-800">
                        ${sumInsuredWithoutLocation.toLocaleString()}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ) : null;
                })()}
                
                {/* Total Row */}
                <TableRow className="border-t-2 border-gray-300 bg-gray-50">
                  <TableCell className="font-semibold">Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right font-semibold">
                    ${(data.quote?.participants?.reduce((total, p) => 
                      total + (Number(p.sumInsured) || 0), 0) || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {(data.quote?.participants?.reduce((total, p) => 
                      total + (Number(p.hectaresAnimalCount) || 0), 0) || 0).toLocaleString()}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
              </Table>
            </div>
          </div>
        )}

      {/* Validation Actions */}
      <div className="space-y-4">
      {!validationComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-2">
            <AlertCircleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Location validation required</p>
              <p>Please validate all locations are correct before proceeding to the next step.</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-center gap-4">
        <Button
          onClick={handleValidateLocations}
          disabled={isValidating}
          className="px-6 py-2"
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Validating Locations...
            </>
          ) : validationComplete ? (
            <>
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Re-validate Locations
            </>
          ) : (
            <>
              <MapIcon className="h-4 w-4 mr-2" />
              Validate Locations
            </>
          )}
        </Button>
        <Button
          onClick={handleValidateAdminBoundaries}
          disabled={isValidatingGeo || !data.quote?.id}
          variant="outline"
          className="px-6 py-2 border-orange-300 text-orange-700 hover:bg-orange-50"
        >
          {isValidatingGeo ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
              Checking Boundaries...
            </>
          ) : (
            <>
              <MapIcon className="h-4 w-4 mr-2" />
              Validate Admin Boundaries
            </>
          )}
        </Button>
      </div>
    </div>

    {/* Validation Results - Only show warning card for issues */}
    {validationComplete && data.quote?.locationValidation && !data.quote.locationValidation.all_within_coverage && (
      <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
        <div className="flex gap-2">
          <AlertCircleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          
          <div>
            <h6 className="font-medium text-yellow-900">
              Location Issues Found
            </h6>
            
            <p className="text-sm mt-1 text-yellow-800">
              {data.quote?.locationValidation?.invalid_locations?.length || 0} location(s) are outside the coverage area. Please review before proceeding.
            </p>
            
            <div className="mt-2">
              <p className="text-sm text-yellow-800 font-medium">Options:</p>
              <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                <li>• Remove invalid locations from the quote</li>
                <li>• Contact support to expand product coverage area</li>
                <li>• Proceed with warning (may affect pricing)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Admin Boundary Validation Results */}
    {geoValidationResults && geoValidationResults.length > 0 && (
      <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
        <h6 className="font-medium text-orange-900 mb-3">Admin Boundary Validation Results</h6>
        <div className="space-y-2">
          {geoValidationResults.map((r) => (
            <div key={r.farmerId} className="flex items-center gap-2 text-sm">
              {r.status === 'valid' ? (
                <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : r.status === 'invalid' ? (
                <AlertCircleIcon className="h-4 w-4 text-orange-600 flex-shrink-0" />
              ) : (
                <InfoIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              )}
              <span className="font-medium">{r.farmerName}</span>
              <span className="text-orange-700">{r.details.reason}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-orange-600 mt-3">
          This check is informational and does not block quote progression.
        </p>
      </div>
    )}

    {/* Note: Removed duplicate error display - now handled by SimpleWizardShell */}
      </div>
    </div>
  );
}
